import { useState } from 'react';
import { Spinner } from 'react-bootstrap';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const REV_DAY = { 1: 'Mon', 2: 'Tue', 3: 'Wed', 4: 'Thu', 5: 'Fri', 6: 'Sat', 7: 'Sun' };

function parseTime(type, expression) {
  let h, m;
  if (type === 'cron') {
    const parts = expression.split(' ');
    h = parseInt(parts[1], 10);
    m = parseInt(parts[0], 10);
  } else {
    const timePart = expression.split('T')[1] || '';
    [h, m] = timePart.split(':').map(Number);
  }
  const h12 = h % 12 || 12;
  return `${String(h12).padStart(2, '0')}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
}

function parseDays(type, expression) {
  if (type !== 'cron') {
    const d = new Date(expression);
    return isNaN(d) ? expression.split('T')[0] : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  }
  const dayStr = expression.split(' ')[4];
  let names;
  if (!dayStr || dayStr === '*' || dayStr === '?') {
    names = Object.values(REV_DAY);
  } else if (dayStr.includes('-')) {
    const [s, e] = dayStr.split('-').map(Number);
    names = Array.from({ length: e - s + 1 }, (_, i) => REV_DAY[i + s]).filter(Boolean);
  } else {
    names = dayStr.split(',').map(Number).map(n => REV_DAY[n]).filter(Boolean);
  }
  return `Every - ${names.join(', ')}`;
}

function getDeviceCount(schedule) {
  return schedule.actions?.[0]?.payload?.devices?.length ?? '—';
}

// ─── Row ──────────────────────────────────────────────────────────────────────

function ScheduleRow({ schedule, index, onEdit, onDelete, onToggle }) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [toggling, setToggling] = useState(false);

  const handleToggle = async () => {
    setToggling(true);
    await onToggle(schedule._id, !schedule.enabled);
    setToggling(false);
  };

  return (
    <tr
      style={{ borderBottom: '1px solid #f3f4f6', transition: 'background 0.1s' }}
      onMouseEnter={e => e.currentTarget.style.background = '#fafafa'}
      onMouseLeave={e => e.currentTarget.style.background = '#fff'}
    >
      <td style={{ ...td, textAlign: 'left' }}>{index + 1}</td>

      <td style={{ ...td, fontWeight: 600, color: '#111827', textAlign: 'left' }}>{schedule.name}</td>

      <td style={td}>{parseTime(schedule.type, schedule.expression)}</td>

      <td style={{ ...td, color: '#6b7280' }}>{parseDays(schedule.type, schedule.expression)}</td>

      <td style={{ ...td, textAlign: 'center' }}>
        <span style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 28, height: 28, borderRadius: 6, background: 'rgba(32,80,255,0.07)',
          fontSize: 12, fontWeight: 700, color: '#2050ff',
        }}>
          {getDeviceCount(schedule)}
        </span>
      </td>

      <td style={td}>
        <span
          onClick={handleToggle}
          title={toggling ? 'Updating…' : `Click to ${schedule.enabled ? 'disable' : 'enable'}`}
          style={{
            fontWeight: 700, fontSize: 13, cursor: toggling ? 'wait' : 'pointer',
            color: schedule.enabled ? '#16a34a' : '#dc2626',
            userSelect: 'none',
          }}
        >
          {toggling ? '…' : (schedule.enabled ? 'Enabled' : 'Disabled')}
        </span>
      </td>

      <td style={{ ...td, whiteSpace: 'nowrap' }}>
        {confirmDelete ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 11, color: '#ef4444', fontWeight: 600 }}>Delete?</span>
            <button
              onClick={() => { onDelete(schedule._id); setConfirmDelete(false); }}
              style={confirmYesBtn}
            >
              Yes
            </button>
            <button onClick={() => setConfirmDelete(false)} style={confirmNoBtn}>No</button>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              onClick={() => onEdit(schedule)}
              title="Edit"
              style={editBtn}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
              </svg>
            </button>
            <button
              onClick={() => setConfirmDelete(true)}
              title="Delete"
              style={deleteBtn}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6"/>
                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                <path d="M10 11v6"/><path d="M14 11v6"/>
                <path d="M9 6V4h6v2"/>
              </svg>
            </button>
          </div>
        )}
      </td>
    </tr>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

function VRVScheduleList({ schedules, loading, onCreateSchedule, onEditSchedule, onDeleteSchedule, onToggleSchedule }) {
  const [search, setSearch] = useState('');

  const filtered = (search.trim()
    ? schedules.filter(s => s.name?.toLowerCase().includes(search.toLowerCase()))
    : [...schedules]
  ).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  return (
    <div style={{ fontFamily: 'Manrope' }}>
      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, gap: 12 }}>
        <div style={{ position: 'relative' }}>
          <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', pointerEvents: 'none', display: 'flex' }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
          </span>
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search schedules…"
            style={{
              border: '1px solid #e5e7eb', borderRadius: 8,
              padding: '7px 12px 7px 30px', fontSize: 13,
              fontFamily: 'Manrope', outline: 'none', color: '#374151', width: 240,
            }}
          />
        </div>
        <button
          onClick={onCreateSchedule}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '8px 16px', background: '#111827', color: '#fff',
            border: 'none', borderRadius: 8, fontWeight: 700,
            fontSize: 13, fontFamily: 'Manrope', cursor: 'pointer',
          }}
          onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
          onMouseLeave={e => e.currentTarget.style.opacity = '1'}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
          Create Schedule
        </button>
      </div>

      {/* Table */}
      <div style={{ overflowX: 'auto', borderRadius: 10, border: '1px solid #f3f4f6' }}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
            <Spinner animation="border" style={{ color: '#2050ff' }} />
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '52px 0', color: '#9ca3af' }}>
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ margin: '0 auto 10px', display: 'block', opacity: 0.35 }}>
              <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
            </svg>
            <div style={{ fontSize: 13, fontWeight: 600 }}>No schedules found</div>
            <div style={{ fontSize: 12, marginTop: 4 }}>Create a schedule to automate IDU controls.</div>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'Manrope' }}>
            <thead>
              <tr style={{ background: '#fff', borderBottom: '2px solid #f3f4f6' }}>
                {['S.No', 'Name', 'Time', 'Days', 'Appliances', 'Status', 'Action'].map(h => (
                  <th key={h} style={th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((s, i) => (
                <ScheduleRow
                  key={s._id}
                  schedule={s}
                  index={i}
                  onEdit={onEditSchedule}
                  onDelete={onDeleteSchedule}
                  onToggle={onToggleSchedule}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const th = {
  padding: '12px 16px',
  fontSize: 12, fontWeight: 600, color: '#9ca3af',
  textAlign: 'center', letterSpacing: '0.04em',
  textTransform: 'uppercase', whiteSpace: 'nowrap',
  background: '#fff',
};

const td = {
  padding: '14px 16px',
  fontSize: 13, color: '#374151',
  verticalAlign: 'middle',
  textAlign: 'center',
};

const editBtn = {
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  width: 32, height: 32, border: '1px solid #e5e7eb', borderRadius: 7,
  background: '#fff', cursor: 'pointer', color: '#6b7280',
};

const deleteBtn = {
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  width: 32, height: 32, border: 'none', borderRadius: 7,
  background: '#ef4444', cursor: 'pointer', color: '#fff',
};

const confirmYesBtn = {
  padding: '3px 10px', background: '#ef4444', color: '#fff',
  border: 'none', borderRadius: 5, fontSize: 11, fontWeight: 700,
  cursor: 'pointer', fontFamily: 'Manrope',
};

const confirmNoBtn = {
  padding: '3px 10px', background: '#f3f4f6', color: '#374151',
  border: 'none', borderRadius: 5, fontSize: 11, fontWeight: 600,
  cursor: 'pointer', fontFamily: 'Manrope',
};

export default VRVScheduleList;
